import { useTranslation } from 'react-i18next'
import { rutaSuperior, urlSitio } from './organizacionesLogica'
import ComercialChip from './ComercialChip'

// A subordinate's name carries its superiors' route in grey ("CODELCO › División
// El Teniente"; approved 2026-10-29). false goes back to the plain name.
const JERARQUIA_EN_NOMBRE = true

// The organizations as a table (a card per organization on a phone).
// Organización always shows; the rest are the user's choice (`columnas`, in
// screen order, see organizacionesLogica.COLUMNAS_OPCIONALES). A click on a row
// opens its card; on "Pertenece a", its superior's; on the website, the site.
//   filas: already filtered, sorted and cut; orden: { campo, dir }; actual: the id whose card is open
export default function ComercialOrganizacionesLista({ filas, columnas, orden, onOrden, actual, onAbrir, nombrePais }) {
  const { t } = useTranslation('comercial')
  const col = (campo, texto) => (
    <th key={campo} className="px-4 py-2.5 font-semibold text-gray-600 whitespace-nowrap"
      aria-sort={orden.campo === campo ? (orden.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button type="button" onClick={() => onOrden(campo)} className="inline-flex items-center gap-1 hover:text-gray-900">
        {texto}
        <span className={`text-[10px] ${orden.campo === campo ? 'text-gray-700' : 'text-gray-300'}`}>
          {orden.campo === campo && orden.dir === 'desc' ? '▼' : '▲'}
        </span>
      </button>
    </th>
  )
  const nada = <span className="text-gray-300">—</span>
  const abrirSuperior = (e, o) => { e.stopPropagation(); onAbrir(o.superior_id) }
  const sitio = (o) => {
    const url = urlSitio(o.sitio_web)
    return url
      ? <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
          className="text-sky-700 hover:underline break-all">{o.sitio_web}</a>
      : nada
  }

  // Each optional column: its header, its cell, and its value on a phone's card.
  const COLUMNAS = {
    tipos: {
      titulo: t('lista.tipos'),
      celda: (o) => (
        <div className="flex flex-wrap gap-1">
          {o.tipos.map(x => <ComercialChip key={x.id} variante="tipo">{x.nombre}</ComercialChip>)}
        </div>
      ),
    },
    superior: {
      titulo: t('lista.perteneceA'),
      celda: (o) => (o.superior
        ? <button type="button" onClick={e => abrirSuperior(e, o)} className="text-left text-gray-600 hover:text-gray-900 hover:underline">
            {rutaSuperior(o)}
          </button>
        : nada),
      texto: (o) => rutaSuperior(o),
    },
    pais: { titulo: t('lista.pais'), clase: 'whitespace-nowrap', celda: (o) => (o.pais ? nombrePais(o.pais) : nada), texto: (o) => (o.pais ? nombrePais(o.pais) : '') },
    ciudad: { titulo: t('lista.ciudad'), celda: (o) => o.ciudad || nada, texto: (o) => o.ciudad },
    sitio_web: { titulo: t('lista.sitioWeb'), celda: sitio, texto: (o) => o.sitio_web },
    identificador: { titulo: t('lista.identificador'), clase: 'whitespace-nowrap', celda: (o) => o.identificador_tributario || nada, texto: (o) => o.identificador_tributario },
    direccion: { titulo: t('lista.direccion'), celda: (o) => o.direccion || nada, texto: (o) => o.direccion },
  }
  const visibles = columnas.filter(c => COLUMNAS[c])

  return (
    <>
      <div className="hidden sm:block bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-anchos-propios>
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-left">
              {col('nombre', t('lista.organizacion'))}
              {visibles.map(c => col(c, COLUMNAS[c].titulo))}
            </tr>
          </thead>
          <tbody>
            {filas.map(o => (
              <tr key={o.id} tabIndex={0} onClick={() => onAbrir(o.id)}
                onKeyDown={e => { if (e.key === 'Enter') onAbrir(o.id) }}
                className={`border-b border-gray-50 last:border-0 cursor-pointer align-top focus:outline-none focus-visible:bg-gray-50
                  ${o.id === actual ? 'bg-sky-50' : 'hover:bg-gray-50'} ${o.archivada ? 'text-gray-400' : 'text-gray-700'}`}>
                <td className="px-4 py-2.5 min-w-[14rem]">
                  <Nombre o={o} />
                </td>
                {visibles.map(c => (
                  <td key={c} className={`px-4 py-2.5 ${COLUMNAS[c].clase || ''}`}>{COLUMNAS[c].celda(o)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="sm:hidden space-y-2">
        {filas.map(o => {
          const datos = visibles.filter(c => c !== 'tipos' && !(c === 'superior' && JERARQUIA_EN_NOMBRE))
            .map(c => COLUMNAS[c].texto(o)).filter(Boolean)
          return (
            <li key={o.id}>
              <button type="button" onClick={() => onAbrir(o.id)}
                className={`w-full text-left bg-white rounded-xl border px-4 py-3 ${o.id === actual ? 'border-sky-300 bg-sky-50' : 'border-gray-200'}`}>
                <Nombre o={o} />
                {visibles.includes('tipos') && o.tipos.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 mt-1.5">
                    {o.tipos.map(x => <ComercialChip key={x.id} variante="tipo">{x.nombre}</ComercialChip>)}
                  </div>
                )}
                {datos.length > 0 && <p className="text-xs text-gray-500 mt-1 break-words">{datos.join(' · ')}</p>}
              </button>
            </li>
          )
        })}
      </ul>
    </>
  )
}

// Its name (with its superiors' route in grey, same size, while JERARQUIA_EN_NOMBRE),
// the archived chip, and the legal name underneath when it says something else.
function Nombre({ o }) {
  const { t } = useTranslation('comercial')
  const ruta = JERARQUIA_EN_NOMBRE ? rutaSuperior(o) : ''
  return (
    <>
      <div className="flex items-start gap-2 min-w-0">
        <span className="min-w-0 break-words">
          {ruta && <span className="text-gray-400">{ruta} › </span>}
          <span className={`font-medium ${o.archivada ? 'text-gray-500' : 'text-gray-900'}`}>{o.nombre_comercial}</span>
        </span>
        {o.archivada && <ComercialChip variante="archivado">{t('chips.archivada')}</ComercialChip>}
      </div>
      {o.razon_social && o.razon_social !== o.nombre_comercial && (
        <p className="text-xs text-gray-500 break-words">{o.razon_social}</p>
      )}
    </>
  )
}
